import {
  ALPHABET,
  ALPHABET_SIZE,
  letterIndex,
  lettersOnly,
  mod,
} from "@/lib/tool-kit/ciphers/alphabet";
import {
  type CipherDirection,
  type KeyedRun,
  type KeyedStep,
  runKeyedLetters,
} from "@/lib/tool-kit/ciphers-polygraphic/keyed-letters";

/**
 * The Beaufort cipher and its variant.
 *
 * With A = 0 … Z = 25, the key letter K and the plain letter P:
 *
 * - **Beaufort:** C = K − P (mod 26). Subtracting the plain letter from the key letter
 *   is its own inverse — P = K − C — so one operation both enciphers and deciphers.
 * - **Variant Beaufort:** C = P − K (mod 26), which is Vigenère *deciphering*. It is not
 *   reciprocal: deciphering adds the key back, P = C + K.
 *
 * The key is the keyword repeated, advancing on letters only. Both use the Vigenère
 * square; Beaufort reads it with the key on the row and the plain letter on the column,
 * the cipher letter being the label at the head of the cell's column.
 */

export type BeaufortKind = "beaufort" | "variant";

/** The cipher-text index for a key index and a plain-text index. */
export function beaufortEncipher(
  kind: BeaufortKind,
  key: number,
  plain: number
): number {
  return mod(kind === "beaufort" ? key - plain : plain - key, ALPHABET_SIZE);
}

/** The plain-text index for a key index and a cipher-text index. */
export function beaufortDecipher(
  kind: BeaufortKind,
  key: number,
  cipher: number
): number {
  return mod(kind === "beaufort" ? key - cipher : cipher + key, ALPHABET_SIZE);
}

function stepFor(
  kind: BeaufortKind,
  direction: CipherDirection,
  input: string,
  keyLetter: string
): KeyedStep {
  const key = letterIndex(keyLetter);
  const value = letterIndex(input);
  if (direction === "encode") {
    const raw = kind === "beaufort" ? key - value : value - key;
    return {
      plain: input,
      key: keyLetter,
      cipher: ALPHABET.charAt(beaufortEncipher(kind, key, value)),
      calc: {
        left: kind === "beaufort" ? key : value,
        op: "−",
        right: kind === "beaufort" ? value : key,
        raw,
      },
    };
  }
  const raw = kind === "beaufort" ? key - value : value + key;
  return {
    plain: ALPHABET.charAt(beaufortDecipher(kind, key, value)),
    key: keyLetter,
    cipher: input,
    calc: {
      left: kind === "beaufort" ? key : value,
      op: kind === "beaufort" ? "−" : "+",
      right: kind === "beaufort" ? value : key,
      raw,
    },
  };
}

export interface BeaufortResult extends KeyedRun {
  /** The keyword's letters in capitals — empty when the keyword has none. */
  key: string;
}

export function beaufortTransform(
  text: string,
  keyword: string,
  kind: BeaufortKind,
  direction: CipherDirection
): BeaufortResult {
  const key = lettersOnly(keyword);
  if (key === "") {
    return { key, output: "", steps: [] };
  }
  const run = runKeyedLetters(text, direction, (letter, position) =>
    stepFor(kind, direction, letter, key.charAt(position % key.length))
  );
  return { key, ...run };
}

/**
 * The square, 26 × 26 and row by row: the row is the key letter, the column the plain
 * letter, and the cell the cipher letter. For Beaufort, row A reads A Z Y X … B.
 */
export function beaufortSquare(kind: BeaufortKind): string[] {
  const cells: string[] = [];
  for (let key = 0; key < ALPHABET_SIZE; key += 1) {
    for (let plain = 0; plain < ALPHABET_SIZE; plain += 1) {
      cells.push(ALPHABET.charAt(beaufortEncipher(kind, key, plain)));
    }
  }
  return cells;
}
